"use client";

import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowRight, Gift, Moon, Play, ShoppingBag, Sparkles, Star } from "lucide-react";
import { MARQUES, COLLECTIONS, PACKS, TOTAL_VISUELS, MAWLID, VEDETTES, VIDEO, SIGNATURE } from "@/data/catalog";
import { prixProduit, formatPrix } from "./types";
import { Crown } from "./ui-extras";
import type { View } from "./types";

interface Props {
  onNav: (v: View) => void;
  onAddProduit: (slug: string, nomMarque: string, num: number, nomProduit: string, image: string) => void;
}

export function Accueil({ onNav, onAddProduit }: Props) {
  const imperiale = MARQUES.find((m) => m.imperiale)!;
  const autres = MARQUES.filter((m) => !m.imperiale);
  const bouye = MARQUES.find((m) => m.slug === "bouye-ndiamsyl")!;

  /* Les 4 coffrets cadeaux Bouye mis en avant pour Mawlid Naby – Gamou */
  const coffrets = bouye.produits.filter((p) => [97, 99, 88, 33].includes(p.num));

  return (
    <div>
      {/* ═══════════ TOP — MAWLID NABY – GAMOU ═══════════ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={MAWLID.hero}
            alt="Célébration Mawlid Naby et Gamou — coffrets cadeaux Bouye de Ndiamsyl sur table impériale devant les rideaux verts et or"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#04180f]/75 via-[#04180f]/55 to-[#04180f]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#04180f]/80 via-transparent to-[#04180f]/55" />
          <div className="absolute inset-x-0 top-0 h-40 lueur-lanterne" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-24 md:py-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex flex-col items-center gap-5"
          >
            <div className="flotte-lent flex items-center gap-3 px-5 py-2 rounded-full border border-[#D4AF37]/55 bg-[#04180f]/70 backdrop-blur-sm">
              <Moon className="h-4 w-4 text-[#F6E27A]" />
              <span className="tracking-[0.35em] text-[#F6E27A] text-[11px] md:text-xs uppercase font-semibold">
                Mawlid Naby <Star className="inline h-3 w-3 -mt-1" /> Gamou
              </span>
              <Sparkles className="h-4 w-4 text-[#F6E27A]" />
            </div>

            <Crown className="h-12 w-20 drop-shadow-[0_4px_20px_rgba(212,175,55,0.5)]" />
            <p className="tracking-[0.45em] text-[#D4AF37] text-xs md:text-sm uppercase">
              Les coffrets cadeaux <span className="gold-text font-bold">Bouye de Ndiamsyl</span> à l&rsquo;honneur
            </p>
            <h1 className="font-display text-5xl md:text-8xl font-black tracking-tight gold-text drop-shadow-[0_6px_30px_rgba(212,175,55,0.3)]">
              L&rsquo;EMPIRE
            </h1>
            <p className="font-display italic text-lg md:text-2xl text-[#F5EFE0]/85 max-w-2xl">
              Sur les rideaux de velours vert et or de Bouye de Ndiamsyl, l&rsquo;Empire
              célèbre Mawlid Naby & le Gamou — {TOTAL_VISUELS} créations, 13 marques,
              un seul éclat de générosité.
            </p>
            <div className="gold-line w-64" />
            <p className="text-sm md:text-base text-[#F6E27A] font-display italic tracking-wide">
              « {SIGNATURE} »
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <a
                href="#coffrets-mawlid"
                className="gold-btn px-7 py-3.5 rounded-xl font-semibold tracking-wide flex items-center justify-center gap-2"
              >
                <Gift className="h-4 w-4" /> Offrir les coffrets Bouye
              </a>
              <button
                onClick={() => onNav({ nom: "marques" })}
                className="px-7 py-3.5 rounded-xl font-semibold tracking-wide flex items-center justify-center gap-2 border border-[#D4AF37]/50 text-[#F6E27A] hover:bg-[#D4AF37]/10 transition-colors"
              >
                Découvrir les 13 marques <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ COFFRETS CADEAUX BOUYE — LA PLACE DU BORDEAUX ═══════════ */}
      <section id="coffrets-mawlid" className="bordeaux-band scroll-mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="text-center mb-12">
            <p className="tracking-[0.35em] text-[#F6E27A] text-xs uppercase mb-3 flex items-center justify-center gap-2">
              <Gift className="h-4 w-4" /> Mawlid Naby – Gamou · Coffrets cadeaux mis en avant
            </p>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-[#F5EFE0]">
              Les <span className="gold-text">Coffrets Bouye de Ndiamsyl</span> du sommet
            </h2>
            <p className="text-[#F5EFE0]/70 max-w-2xl mx-auto mt-4">
              Velours vert émeraude, or impérial et rubans bordeaux — quatre coffrets
              de gala choisis par la Maison pour les nuits bénies du Mawlid et du Gamou.
            </p>
            <div className="gold-line w-72 mx-auto mt-6" />
          </div>

          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8 items-stretch">
            <motion.figure
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="card-empire rounded-2xl overflow-hidden flex flex-col"
            >
              <img src={MAWLID.visuels[0].file} alt={MAWLID.visuels[0].nom} className="w-full flex-1 object-cover min-h-64" loading="lazy" />
              <figcaption className="p-5">
                <p className="font-display font-bold text-lg text-[#F5EFE0]">{MAWLID.visuels[0].nom}</p>
                <p className="text-sm text-[#F5EFE0]/60 mt-1">{MAWLID.visuels[0].desc}</p>
              </figcaption>
            </motion.figure>

            <div className="grid sm:grid-cols-2 gap-4">
              {coffrets.map((p, i) => (
                <motion.div
                  key={p.num}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="card-bordeaux rounded-2xl overflow-hidden flex flex-col"
                >
                  <div className="relative">
                    <img src={p.file} alt={p.name} className="w-full aspect-square object-cover" loading="lazy" />
                    <span className="absolute top-2.5 left-2.5 px-2.5 py-1 rounded-full text-[10px] tracking-widest uppercase bg-[#2a040d]/85 border border-[#D4AF37]/50 text-[#F6E27A]">
                      Coffret de gala
                    </span>
                  </div>
                  <div className="p-4 flex flex-col gap-2.5 flex-1">
                    <h3 className="font-display font-bold text-[#F5EFE0] leading-snug">{p.name}</h3>
                    <div className="mt-auto flex items-center justify-between gap-2">
                      <span className="font-display text-xl font-bold gold-text">{formatPrix(prixProduit(p.num))}</span>
                      <button
                        onClick={() => onAddProduit("bouye-ndiamsyl", bouye.nom, p.num, p.name, p.file)}
                        className="gold-btn px-4 py-2 rounded-xl font-semibold text-xs inline-flex items-center gap-1.5"
                        aria-label={"Ajouter " + p.name + " au panier"}
                      >
                        <ShoppingBag className="h-3.5 w-3.5" /> Ajouter
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => onNav({ nom: "marque", slug: "bouye-ndiamsyl" })}
              className="px-7 py-3.5 rounded-xl font-semibold tracking-wide inline-flex items-center gap-2 border border-[#D4AF37]/50 text-[#F6E27A] hover:bg-[#D4AF37]/10 transition-colors"
            >
              Visiter la boutique Bouye de Ndiamsyl — 99 créations <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════ PRODUITS VEDETTES — CARROUSEL ZOOM CENTRAL ═══════════ */}
      <CarrouselVedettes onAddProduit={onAddProduit} onNav={onNav} />

      {/* ═══════════ VIDÉO IMPÉRIALE ═══════════ */}
      <section className="border-t border-[#D4AF37]/15">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="text-center mb-10">
            <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3 flex items-center justify-center gap-2">
              <Play className="h-4 w-4" /> Le film de l&rsquo;Empire
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#F5EFE0]">
              Une <span className="gold-text">vidéo impériale</span> — Mawlid Naby & Gamou
            </h2>
            <p className="text-[#F5EFE0]/60 max-w-xl mx-auto mt-3">
              Des lanternes dorées aux coffrets de gala : {VIDEO.duree} de cinématique
              à travers les couleurs de l&rsquo;Empire.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="card-empire rounded-3xl overflow-hidden shadow-[0_30px_90px_rgba(0,0,0,0.55)]"
          >
            <video
              controls
              preload="metadata"
              poster={VIDEO.poster}
              className="w-full aspect-video bg-black"
              aria-label={VIDEO.titre}
            >
              <source src={VIDEO.file} type="video/mp4" />
              Votre navigateur ne peut pas lire la vidéo impériale.
            </video>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ CHIFFRES ═══════════ */}
      <section className="border-y border-[#D4AF37]/20 bg-[#03150d]/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#D4AF37]/15">
          {[
            ["13", "marques de prestige"],
            [String(TOTAL_VISUELS), "créations photoréalistes"],
            ["99", "créations Bouye de Ndiamsyl"],
            ["8", "portails partenaires"],
          ].map(([n, l]) => (
            <div key={l} className="py-8 px-2 text-center">
              <p className="font-display text-3xl md:text-4xl font-bold gold-text">{n}</p>
              <p className="text-xs md:text-sm text-[#F5EFE0]/60 mt-1">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════ VITRINE FINALE ═══════════ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="card-empire rounded-2xl overflow-hidden">
              <img
                src={COLLECTIONS[0].file}
                alt={COLLECTIONS[0].nom}
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="space-y-5"
          >
            <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> La pièce maîtresse
            </p>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-[#F5EFE0] leading-tight">
              La Vitrine Finale <span className="gold-text">de l&rsquo;Empire</span>
            </h2>
            <p className="text-[#F5EFE0]/70 leading-relaxed">
              Treize échoppes de velours illuminées en fer à cheval, l&rsquo;amphore
              impériale Ndiamsyl Mody trônant sous le dais couronné, et les packs
              e-commerce scellés à la cire d&rsquo;or — la réunion solennelle de tout
              un empire dans une seule salle de palais.
            </p>
            <ul className="space-y-2 text-sm text-[#F5EFE0]/65">
              <li>· Chaque niche bruisse de la couleur d&rsquo;une marque</li>
              <li>· Couronne royale et ruban ivoire sur chaque étiquette</li>
              <li>· Signature complète apposée sur l&rsquo;ensemble des créations</li>
            </ul>
            <button
              onClick={() => onNav({ nom: "marques" })}
              className="gold-btn px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2"
            >
              Visiter les échoppes <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ LES MARQUES ═══════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20 md:pb-28">
        <div className="text-center mb-12">
          <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3">L&rsquo;orgueil de l&rsquo;Empire</p>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[#F5EFE0]">
            Les <span className="gold-text">13 Marques</span> & la Maison Impériale
          </h2>
          <div className="gold-line w-72 mx-auto mt-5" />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <MarqueCard m={imperiale} imperiale onOpen={() => onNav({ nom: "marque", slug: imperiale.slug })} />
          {autres.map((m) => (
            <MarqueCard key={m.slug} m={m} onOpen={() => onNav({ nom: "marque", slug: m.slug })} />
          ))}
        </div>
      </section>

      {/* ═══════════ COLLECTIONS ═══════════ */}
      <section className="border-t border-[#D4AF37]/15 bg-[#03150d]/60 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3">Les vitrines de collection</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#F5EFE0]">
              Les <span className="gold-text">Grandes Toiles</span> de l&rsquo;Empire
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {COLLECTIONS.map((c) => (
              <figure key={c.file} className="card-empire rounded-2xl overflow-hidden">
                <img src={c.file} alt={c.nom} className="w-full aspect-square object-cover" loading="lazy" />
                <figcaption className="p-4">
                  <p className="font-display font-semibold text-[#F5EFE0]">{c.nom}</p>
                  <p className="text-xs text-[#F5EFE0]/55 mt-1">{c.sousTitre}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ PACKS TEASER ═══════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28">
        <div className="text-center mb-10">
          <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3">Prêts pour la boutique en ligne</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#F5EFE0]">
            Les <span className="gold-text">Packs E-commerce</span> Impériaux
          </h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          {PACKS.slice(0, 3).map((p) => (
            <figure key={p.file} className="card-empire rounded-2xl overflow-hidden">
              <img src={p.file} alt={p.nom} className="w-full aspect-square object-cover" loading="lazy" />
              <figcaption className="p-4">
                <p className="font-display font-semibold text-[#F5EFE0]">{p.nom}</p>
                <p className="text-xs text-[#F5EFE0]/55 mt-1 line-clamp-2">{p.desc}</p>
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="text-center mt-10">
          <button
            onClick={() => onNav({ nom: "packs" })}
            className="gold-btn px-7 py-3.5 rounded-xl font-semibold inline-flex items-center gap-2"
          >
            Voir tous les packs <ShoppingBag className="h-4 w-4" />
          </button>
        </div>
      </section>
    </div>
  );
}

/* ─────────────── Carrousel des vedettes — zoom du produit au centre ─────────────── */
function CarrouselVedettes({ onAddProduit, onNav }: Props) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center", skipSnaps: false });
  const [centre, setCentre] = useState(0);
  const [pause, setPause] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCentre(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || pause) return;
    const id = window.setInterval(() => emblaApi.scrollNext(), 3600);
    return () => window.clearInterval(id);
  }, [emblaApi, pause]);

  const defiler = useCallback(
    (dir: 1 | -1) => {
      if (dir === 1) emblaApi?.scrollNext();
      else emblaApi?.scrollPrev();
    },
    [emblaApi]
  );

  return (
    <section
      className="border-t border-[#D4AF37]/15 py-16 md:py-24 overflow-hidden"
      onMouseEnter={() => setPause(true)}
      onMouseLeave={() => setPause(false)}
      aria-label="Carrousel des produits vedettes"
    >
      <div className="text-center mb-10 px-4">
        <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3">Après le top — les vedettes défilent</p>
        <h2 className="font-display text-3xl md:text-5xl font-bold text-[#F5EFE0]">
          Les <span className="gold-text">Produits Vedettes</span> de l&rsquo;Empire
        </h2>
        <p className="text-[#F5EFE0]/60 max-w-xl mx-auto mt-3">
          Le produit central s&rsquo;agrandit sous vos yeux — cliquez sur ses flancs
          ou laissez le défilé suivre son cours.
        </p>
        <div className="gold-line w-72 mx-auto mt-5" />
      </div>

      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex items-center py-6">
            {VEDETTES.map((v, i) => (
              <div key={v.marqueSlug + v.num} className="flex-[0_0_80%] sm:flex-[0_0_46%] lg:flex-[0_0_32%] px-3 md:px-4">
                <div
                  className={"slide-vedette rounded-2xl " + (i === centre ? "est-centre" : "")}
                >
                  <figure className="card-empire rounded-2xl overflow-hidden h-full bg-[#03150d]/60">
                    <img
                      src={v.file}
                      alt={v.nom + " — " + v.marque}
                      className="w-full aspect-square object-cover"
                      loading="lazy"
                    />
                    <figcaption className="p-4 md:p-5 space-y-2.5">
                      <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">{v.marque}</p>
                      <h3 className="font-display font-bold text-base md:text-lg text-[#F5EFE0] leading-snug">{v.nom}</h3>
                      <p className="text-xs text-[#F5EFE0]/55 italic">{v.accroche}</p>
                      <div className="flex items-center justify-between gap-2 pt-1">
                        <span className="font-display text-lg md:text-xl font-bold gold-text">{formatPrix(prixProduit(v.num))}</span>
                        <button
                          onClick={() => onAddProduit(v.marqueSlug, v.marque, v.num, v.nom, v.file)}
                          className="gold-btn px-4 py-2 rounded-xl font-semibold text-xs inline-flex items-center gap-1.5"
                          aria-label={"Ajouter " + v.nom + " au panier"}
                        >
                          <ShoppingBag className="h-3.5 w-3.5" /> Ajouter
                        </button>
                      </div>
                    </figcaption>
                  </figure>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => defiler(-1)}
          aria-label="Vedette précédente"
          className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-[#04180f]/90 border border-[#D4AF37]/50 text-[#F6E27A] hover:bg-[#D4AF37] hover:text-[#170d02] transition-colors flex items-center justify-center"
        >
          ‹
        </button>
        <button
          onClick={() => defiler(1)}
          aria-label="Vedette suivante"
          className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-[#04180f]/90 border border-[#D4AF37]/50 text-[#F6E27A] hover:bg-[#D4AF37] hover:text-[#170d02] transition-colors flex items-center justify-center"
        >
          ›
        </button>
      </div>

      <div className="flex items-center justify-center gap-2 mt-6" role="tablist" aria-label="Position dans le carrousel">
        {VEDETTES.map((v, i) => (
          <button
            key={"point-" + v.marqueSlug + v.num}
            role="tab"
            aria-selected={i === centre}
            aria-label={"Aller à la vedette " + v.nom}
            onClick={() => emblaApi?.scrollTo(i)}
            className={
              "h-2 rounded-full transition-all duration-300 " +
              (i === centre ? "w-8 bg-gradient-to-r from-[#F6E27A] to-[#D4AF37]" : "w-2 bg-[#D4AF37]/35 hover:bg-[#D4AF37]/60")
            }
          />
        ))}
      </div>

      <div className="text-center mt-8">
        <button
          onClick={() => onNav({ nom: "marques" })}
          className="px-6 py-3 rounded-xl font-semibold text-sm inline-flex items-center gap-2 border border-[#D4AF37]/50 text-[#F6E27A] hover:bg-[#D4AF37]/10 transition-colors"
        >
          Explorer les 13 boutiques <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
}

/* ─────────────── Carte de marque ─────────────── */
function MarqueCard({ m, onOpen, imperiale }: { m: import("@/data/catalog").Marque; onOpen: () => void; imperiale?: boolean }) {
  return (
    <motion.button
      onClick={onOpen}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="card-empire rounded-2xl overflow-hidden text-left group focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60"
      aria-label={"Découvrir la marque " + m.nom}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={m.image}
          alt={"Création vedette — " + m.nom}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
        {imperiale && (
          <span className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#170d02]/85 border border-[#D4AF37]/60 text-[#F6E27A] text-[11px] tracking-widest uppercase">
            <Crown className="h-3.5 w-5" /> Maison Impériale
          </span>
        )}
        <span
          className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full text-[11px] font-semibold border backdrop-blur-sm"
          style={{ background: m.couleur + "CC", borderColor: m.couleurDouce, color: m.texte }}
        >
          {m.produits.length} créations
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-display text-lg md:text-xl font-bold text-[#F5EFE0] group-hover:text-[#F6E27A] transition-colors">
          {m.nom}
        </h3>
        <p className="text-xs md:text-sm text-[#F5EFE0]/55 mt-1.5 line-clamp-2">{m.univers}</p>
      </div>
    </motion.button>
  );
}
